import { useState, useEffect } from 'react';
import { Search, BookOpen, Eye, X } from 'lucide-react';
import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Skill } from '@/types';

const categories = [
  'autonomous-ai-agents',
  'creative',
  'devops',
  'email',
  'github',
  'mlops',
  'mlops/evaluation',
  'mlops/inference',
  'note-taking',
  'productivity',
  'research',
  'smart-home',
  'social-media',
  'software-development',
];

export function Skills() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);

  useEffect(() => {
    loadSkills();
  }, [categoryFilter]);

  const loadSkills = async () => {
    try {
      setLoading(true);
      const res = await apiClient.listSkills(categoryFilter || undefined);
      setSkills(res.skills || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  const filteredSkills = skills.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Skills</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Browse and manage Anika Agent skills</p>
        </div>
        <Button onClick={loadSkills} loading={loading}>
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700 p-4 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search skills..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">All Categories</option>
          {categories.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Skills Grid */}
      <div className="bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700 overflow-hidden">
        {error && (
          <div className="p-4 text-red-600 dark:text-red-400 border-b border-gray-200 dark:border-dark-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          </div>
        ) : filteredSkills.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No skills found</p>
          </div>
        ) : (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredSkills.map((skill) => (
              <div
                key={skill.name}
                className="bg-gray-50 dark:bg-dark-800/50 rounded-lg border border-gray-200 dark:border-dark-700 p-4 hover:border-primary-300 dark:hover:border-primary-700 transition-colors cursor-pointer"
                onClick={() => setSelectedSkill(skill)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">{skill.name}</h3>
                      {skill.is_pinned && (
                        <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 rounded">
                          Pinned
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">
                      {skill.description || 'No description'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-dark-700 rounded">{skill.category}</span>
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-dark-700 rounded capitalize">{skill.source}</span>
                      {skill.version && <span>v{skill.version}</span>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setSelectedSkill(skill); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Skill Detail Modal */}
      {selectedSkill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedSkill(null)}>
          <div className="bg-white dark:bg-dark-900 rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 dark:border-dark-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BookOpen className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedSkill.name}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{selectedSkill.category} • {selectedSkill.source}</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelectedSkill(null)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="prose dark:prose-invert max-w-none">
                <pre className="whitespace-pre-wrap text-sm bg-gray-100 dark:bg-dark-800 p-4 rounded-lg overflow-x-auto">
                  {JSON.stringify(selectedSkill.frontmatter, null, 2)}
                </pre>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-dark-700 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelectedSkill(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}